import { createAsyncThunk } from '@reduxjs/toolkit';

import type {
  ArbitragemDetalhe,
  ArbitragemPendencia,
  ArbitragemPendenciaResolucao,
  ArbitragemPendenciaResolucaoResultado,
} from '../types/arbitragemDetalheTypes';

import type {
  CitacaoArbitralAuthChannel,
  CitacaoArbitralDetail,
  CitacaoArbitralKycInitiateResponse,
  CitacaoArbitralKycStatusResponse,
  CitacaoArbitralKycUpdateContactResponse,
  CitacaoArbitralListItem,
  CitacaoArbitralMagicLinkConsumeResponse,
  CitacaoArbitralMagicLinkStatusResponse,
  CitacaoArbitralRequestFallbackResponse,
  CitacaoArbitralRequestMagicLinkResponse,
  CitacaoArbitralSubmitPayload,
  CitacaoArbitralSubmitResponse,
  CitacaoArbitralUploadResult,
  CitacaoArbitralValidateHashResponse,
  ConviteAdvogadoEmitirPayload,
  ConviteAdvogadoEmitirResponse,
  PortalFeedResponse,
  SolicitarTrocaEspecialistaPayload,
  SolicitarTrocaEspecialistaResponse,
} from '../types/portalArbitralTypes';

import { citacaoArbitralService } from './services';

export const convidarAdvogadoPorEmail = createAsyncThunk<
  ConviteAdvogadoEmitirResponse,
  { parteArbitralId: string; payload: ConviteAdvogadoEmitirPayload },
  { rejectValue: string }
>('citacaoArbitral/convidarAdvogado', async ({ parteArbitralId, payload }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.convidarAdvogado(parteArbitralId, payload);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Não foi possível enviar o convite',
    );
  }
});

export const arbitragemSolicitarTrocaEspecialista = createAsyncThunk<
  SolicitarTrocaEspecialistaResponse,
  { parteArbitralId: string; payload: SolicitarTrocaEspecialistaPayload },
  { rejectValue: string }
>(
  'citacaoArbitral/trocarEspecialista',
  async ({ parteArbitralId, payload }, { rejectWithValue }) => {
    try {
      return await citacaoArbitralService.solicitarTrocaEspecialista(parteArbitralId, payload);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Não foi possível enviar a solicitação',
      );
    }
  },
);

export const citacaoArbitralValidateHash = createAsyncThunk<
  CitacaoArbitralValidateHashResponse,
  { hash: string },
  { rejectValue: string }
>('citacaoArbitral/validateHash', async ({ hash }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.validateOnboardingHash(hash);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralRequestMagicLink = createAsyncThunk<
  CitacaoArbitralRequestMagicLinkResponse,
  { hash: string; canal: CitacaoArbitralAuthChannel },
  { rejectValue: string }
>('citacaoArbitral/requestMagicLink', async ({ hash, canal }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.requestMagicLink(hash, canal);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralConsumeMagicLink = createAsyncThunk<
  CitacaoArbitralMagicLinkConsumeResponse,
  { token: string },
  { rejectValue: string }
>('citacaoArbitral/consumeMagicLink', async ({ token }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.consumeMagicLink(token);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralCheckMagicLinkStatus = createAsyncThunk<
  CitacaoArbitralMagicLinkStatusResponse,
  { hash: string; pollToken: string },
  { rejectValue: string }
>('citacaoArbitral/checkMagicLinkStatus', async ({ hash, pollToken }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.checkMagicLinkStatus(hash, pollToken);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralRequestFallback = createAsyncThunk<
  CitacaoArbitralRequestFallbackResponse,
  { hash: string; currentChannel: CitacaoArbitralAuthChannel },
  { rejectValue: string }
>('citacaoArbitral/requestFallback', async ({ hash, currentChannel }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.requestFallback(hash, currentChannel);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralInitiateKyc = createAsyncThunk<
  CitacaoArbitralKycInitiateResponse,
  { hash: string },
  { rejectValue: string }
>('citacaoArbitral/initiateKyc', async ({ hash }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.initiateKyc(hash);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralGetKycStatus = createAsyncThunk<
  CitacaoArbitralKycStatusResponse,
  { verificationId: string },
  { rejectValue: string }
>('citacaoArbitral/getKycStatus', async ({ verificationId }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.getKycStatus(verificationId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralUpdateContactAfterKyc = createAsyncThunk<
  CitacaoArbitralKycUpdateContactResponse,
  { hash: string; verificationId: string; novoTelefone: string },
  { rejectValue: string }
>(
  'citacaoArbitral/updateContactAfterKyc',
  async ({ hash, verificationId, novoTelefone }, { rejectWithValue }) => {
    try {
      return await citacaoArbitralService.updateContactAfterKyc(hash, verificationId, novoTelefone);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const citacaoArbitralList = createAsyncThunk<
  CitacaoArbitralListItem[],
  void,
  { rejectValue: string }
>('citacaoArbitral/list', async (_arg, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.listCitacoes();
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralGetDetail = createAsyncThunk<
  CitacaoArbitralDetail,
  { id: string },
  { rejectValue: string }
>('citacaoArbitral/getDetail', async ({ id }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.getDetail(id);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const arbitragemGetDetalhe = createAsyncThunk<
  ArbitragemDetalhe,
  { id: string },
  { rejectValue: string }
>('citacaoArbitral/getArbitragemDetalhe', async ({ id }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.getArbitragemDetalhe(id);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const arbitragemResolverPendencia = createAsyncThunk<
  ArbitragemPendenciaResolucaoResultado,
  {
    citacaoId: string;
    pendencia: ArbitragemPendencia;
    resolucao: ArbitragemPendenciaResolucao;
  },
  { rejectValue: string }
>(
  'citacaoArbitral/resolverPendencia',
  async ({ citacaoId, pendencia, resolucao }, { rejectWithValue }) => {
    try {
      return await citacaoArbitralService.resolverPendencia(citacaoId, pendencia, resolucao);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const citacaoArbitralSubmit = createAsyncThunk<
  CitacaoArbitralSubmitResponse,
  { id: string; payload: CitacaoArbitralSubmitPayload },
  { rejectValue: string }
>('citacaoArbitral/submit', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.submeterManifestacao(id, payload);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralUploadAnexo = createAsyncThunk<
  CitacaoArbitralUploadResult,
  { id: string; file: File },
  { rejectValue: string }
>('citacaoArbitral/uploadAnexo', async ({ id, file }, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.uploadAnexo(id, file);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const citacaoArbitralGetFeed = createAsyncThunk<
  PortalFeedResponse,
  void,
  { rejectValue: string }
>('citacaoArbitral/getFeed', async (_arg, { rejectWithValue }) => {
  try {
    return await citacaoArbitralService.getFeed();
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});
